import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Home } from 'lucide-react';
import AdminGuard from '@/components/admin/AdminGuard';
import AdminSidebar, { AdminSection } from '@/components/admin/AdminSidebar';
import AdminDashboard from '@/components/admin/AdminDashboard';
import AdminFigurines from '@/components/admin/AdminFigurines';
import AdminCategories from '@/components/admin/AdminCategories';
import AdminUsers from '@/components/admin/AdminUsers';
import AdminOrders from '@/components/admin/AdminOrders';

export default function AdminPanel() {
  const [section, setSection] = useState<AdminSection>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const SECTIONS: Record<AdminSection, JSX.Element> = {
    dashboard: <AdminDashboard />,
    figurines: <AdminFigurines />,
    orders:    <AdminOrders />,
    categories: <AdminCategories />,
    users: <AdminUsers />,
  };

  return (
    <AdminGuard>
      <div className="flex min-h-screen bg-background">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex flex-shrink-0">
          <AdminSidebar active={section} onChange={setSection} />
        </div>

        {/* Mobile Sidebar overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <>
              <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40 lg:hidden"
                style={{ background: 'oklch(0.05 0.01 280 / 0.75)' }}
                onClick={() => setSidebarOpen(false)}
              />
              <motion.div
                key="sidebar"
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: 'spring', stiffness: 320, damping: 35 }}
                className="fixed top-0 left-0 z-50 lg:hidden"
              >
                <AdminSidebar active={section} onChange={(s) => { setSection(s); setSidebarOpen(false); }} />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-h-screen overflow-auto">
          {/* Mobile top bar */}
          <div
            className="lg:hidden flex items-center gap-4 px-4 py-3 border-b sticky top-0 z-30"
            style={{ background: 'oklch(0.10 0.022 280 / 0.95)', borderColor: 'oklch(0.22 0.04 280)', backdropFilter: 'blur(12px)' }}
          >
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-sm font-black tracking-widest flex-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              ADMIN PANEL
            </span>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
              style={{ fontFamily: 'Rajdhani, sans-serif', color: 'var(--accent)', background: 'oklch(0.68 0.28 195 / 0.12)', border: '1px solid oklch(0.68 0.28 195 / 0.3)' }}
            >
              <Home className="w-3.5 h-3.5" />
              Strona główna
            </button>
          </div>

          {/* Section content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={section}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
              className="flex-1"
            >
              {SECTIONS[section]}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </AdminGuard>
  );
}
